import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { loadPinnedDocument, loadCatalogDocument, type DocumentSelection, type CatalogDocumentSelection } from "./document";
import { PdfPreview } from "./PdfPreview";

const imageTypes = new Set(["image/png", "image/jpeg", "image/webp", "image/gif", "image/avif"]);

export function DocumentView({ selection, onClose }: { selection: DocumentSelection | CatalogDocumentSelection; onClose: () => void }) {
  const key = JSON.stringify(selection);
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{ key: string; name?: string; text?: string; url?: string; image?: boolean; pdf?: Uint8Array; failed?: boolean }>();
  useEffect(() => {
    let controller: AbortController;
    let url: string | undefined;
    function discard() {
      controller?.abort();
      if (url) URL.revokeObjectURL(url);
      url = undefined;
    }
    function read() {
      discard();
      controller = new AbortController();
      const signal = controller.signal;
      setState(undefined);
      const load = "catalog" in selection ? loadCatalogDocument(selection, signal) : loadPinnedDocument(selection, signal);
      void load.then((document) => {
        if (signal.aborted) return;
        const image = imageTypes.has(document.contentType);
        let text: string | undefined;
        if (document.contentType.startsWith("text/") && document.bytes.length <= 4 << 20) {
          try {
            const decoded = new TextDecoder("utf-8", { fatal: true }).decode(document.bytes);
            if (!decoded.includes("\0")) text = decoded;
          } catch { /* Binary content remains explicitly downloadable. */ }
        }
        // Active formats are download-only. Never attach HTML/SVG to an iframe,
        // object element or same-origin document, even with a valid checksum.
        url = URL.createObjectURL(new Blob([document.bytes], { type: image ? document.contentType : "application/octet-stream" }));
        setState({ key, name: document.name, text, url, image, pdf: document.contentType === "application/pdf" ? document.bytes : undefined });
      }).catch(() => {
        if (!signal.aborted) {
          if (url) URL.revokeObjectURL(url);
          url = undefined;
          setState({ key, failed: true });
        }
      });
    }
    read();
    window.addEventListener("focus", read);
    return () => { window.removeEventListener("focus", read); discard(); };
  }, [key, revision]);
  const current = state?.key === key ? state : undefined;
  const Container = "catalog" in selection ? "section" : "main";
  return <Container className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-8 sm:py-12 text-[#20272b]">
    <Button variant="ghost" onClick={onClose}>{"catalog" in selection ? "Back to documents" : "Back to conversation"}</Button>
    {!current ? <p className="mt-6 text-sm" role="status">Opening document…</p> : current.failed ? <>
      <h1 className="mt-6 text-xl font-semibold">Document unavailable</h1>
      <p className="mt-3 text-sm leading-6 text-[#52606e]">This saved version couldn't be opened. Your access may have changed.</p>
      <Button className="mt-5" onClick={() => setRevision((value) => value + 1)}>Try again</Button>
    </> : <>
      <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
        <h1 className="min-w-0 break-words text-xl font-semibold">{current.name}</h1>
        <a className="text-sm underline" href={current.url} download={current.name}>Download document</a>
      </div>
      {current.pdf ? <PdfPreview key={current.url} bytes={current.pdf} /> : current.text !== undefined ? <pre className="mt-8 whitespace-pre-wrap break-words font-sans text-sm leading-7">{current.text}</pre> : current.image ?
        <img className="mt-8 h-auto max-w-full" src={current.url} alt="Saved document image" onError={() => setState((value) => value?.key === key ? { ...value, image: false } : value)} /> :
        <p className="mt-8 text-sm leading-6 text-[#52606e]">A preview isn't available for this format. Download the saved version to open it.</p>}
    </>}
  </Container>;
}
